import {
  Box,
  Button,
  HStack,
  Input,
  Link,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FileText, Info } from "lucide-react";
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTRPC } from "../../trpc/client";
import { useMerchant } from "../../hooks/useMerchant";
import { toaster } from "../ui/toaster";
import { WHATSAPP_SUPPORT_URL } from "../../config/tutorials";
import { OnboardingComboBox } from "../../containers/public/onboarding/components";
import { OnboardingSelect } from "../../containers/public/onboarding/components";
import {
  ONBOARDING_LINKS,
  tipoCuentaCollection,
  tipoDocumentoTitularCollection,
} from "../../containers/public/onboarding/constants";

const accountTypeLabels = { savings: "Ahorros", checking: "Corriente" };
const documentTypeLabels = { nit: "NIT", cc: "Cédula de ciudadanía" };

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const BankAccountTab = (): JSX.Element => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { merchant } = useMerchant();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    bank: "",
    accountType: "",
    accountNumber: "",
    accountHolderName: "",
    accountHolderDocumentType: "",
    accountHolderDocumentNumber: "",
  });
  const [certificationFile, setCertificationFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: pending, isLoading } = useQuery({
    ...trpc.bankAccountRequests.getPending.queryOptions({
      rentalsMerchantId: merchant?.id ?? "",
    }),
    enabled: !!merchant?.id,
  });
  const { data: catalogs } = useQuery(trpc.onboarding.catalogs.queryOptions());
  const { data: pseServiceCode } = useQuery({
    ...trpc.merchants.gatewayServiceCode.queryOptions({
      rentalsMerchantId: merchant?.id ?? "",
    }),
    enabled: !!merchant?.gateway,
  });

  const bankLabel = (bank: string) =>
    catalogs?.banks.find((b) => b.value === bank)?.label ?? bank;

  const notify = (
    title: string,
    description: string,
    type: "success" | "error"
  ) =>
    toaster.create({
      title,
      description,
      type,
      duration: 5000,
      meta: { closable: true },
    });

  const { mutateAsync: proposeBankAccount } = useMutation(
    trpc.bankAccountRequests.propose.mutationOptions()
  );
  const { mutateAsync: getCertificationUploadUrl } = useMutation(
    trpc.bankAccountRequests.getCertificationUploadUrl.mutationOptions()
  );
  const cancelRequest = useMutation(
    trpc.bankAccountRequests.cancel.mutationOptions({
      onSuccess: async () => {
        if (!merchant) return;
        await queryClient.invalidateQueries({
          queryKey: trpc.bankAccountRequests.getPending.queryKey({
            rentalsMerchantId: merchant.id,
          }),
        });
        notify(
          "Solicitud cancelada",
          "Puedes proponer un nuevo cambio de cuenta.",
          "success"
        );
      },
      onError: (error) => notify("Error", error.message, "error"),
    })
  );

  const handleSubmit = async () => {
    if (!merchant || !certificationFile) return;
    setIsSubmitting(true);
    try {
      const { id } = await proposeBankAccount({
        rentalsMerchantId: merchant.id,
        bank: formData.bank as never,
        accountType: formData.accountType as "savings" | "checking",
        accountNumber: formData.accountNumber,
        accountHolderName: formData.accountHolderName.trim(),
        accountHolderDocumentType: formData.accountHolderDocumentType as
          | "nit"
          | "cc",
        accountHolderDocumentNumber: formData.accountHolderDocumentNumber,
      });
      const { uploadUrl } = await getCertificationUploadUrl({
        rentalsMerchantId: merchant.id,
        id,
      });
      await fetch(uploadUrl, {
        method: "PUT",
        body: certificationFile,
        headers: { "Content-Type": "application/pdf" },
      });
      queryClient.invalidateQueries({
        queryKey: trpc.bankAccountRequests.getPending.queryKey({
          rentalsMerchantId: merchant.id,
        }),
      });
      setFormData({
        bank: "",
        accountType: "",
        accountNumber: "",
        accountHolderName: "",
        accountHolderDocumentType: "",
        accountHolderDocumentNumber: "",
      });
      setCertificationFile(null);
      notify(
        "Solicitud enviada",
        "Un administrador de Palomma revisará el cambio.",
        "success"
      );
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : "Hubo un error al enviar la solicitud.";
      notify("Error", message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () =>
    !!formData.bank &&
    !!formData.accountType &&
    /^\d{8,30}$/.test(formData.accountNumber) &&
    formData.accountHolderName.trim().length > 0 &&
    (formData.accountHolderDocumentType === "nit" ||
      formData.accountHolderDocumentType === "cc") &&
    /^\d{5,20}$/.test(formData.accountHolderDocumentNumber) &&
    !!certificationFile;

  return (
    <VStack gap={4} align="stretch">
      <Box>
        <Text fontWeight={600}>Cuenta bancaria</Text>
        <Text fontSize="sm" color="gray.500" mt={0.5}>
          Propón un cambio de cuenta para recibir tus desembolsos
        </Text>

        {merchant?.merchantAccount && (
          <Box
            mt={3}
            borderWidth="1px"
            borderColor="gray.200"
            bg="gray.50"
            borderRadius="md"
            px={4}
            py={3}
          >
            <Text fontWeight={600} fontSize="sm">
              Cuenta actual
            </Text>
            <Text fontSize="sm" color="gray.700" mt={1}>
              {bankLabel(merchant.merchantAccount.bank)} ·{" "}
              {accountTypeLabels[merchant.merchantAccount.accountType]} · ****
              {merchant.merchantAccount.accountNumber}
              {merchant.merchantAccount.accountHolderName &&
                ` · ${merchant.merchantAccount.accountHolderName}`}
            </Text>
          </Box>
        )}


      </Box>

      {isLoading ? (
        <Text fontSize="sm" color="gray.500">
          Cargando...
        </Text>
      ) : pending ? (
        <Box
          borderWidth="1px"
          borderColor="orange.200"
          bg="orange.50"
          borderRadius="md"
          px={4}
          py={3}
        >
          <HStack justify="space-between" align="start" gap={4}>
            <Box>
              <Text fontWeight={600} fontSize="sm">
                Cambio pendiente de aprobación
              </Text>
              <Text fontSize="sm" mt={1}>
                {bankLabel(pending.bank)} ·{" "}
                {accountTypeLabels[pending.accountType]} ·{" "}
                {pending.accountNumber}
                {pending.accountHolderName &&
                  ` · ${pending.accountHolderName}${
                    pending.accountHolderDocumentType &&
                    pending.accountHolderDocumentNumber
                      ? ` (${
                          documentTypeLabels[pending.accountHolderDocumentType]
                        } ${pending.accountHolderDocumentNumber})`
                      : ""
                  }`}
              </Text>
              <Text fontSize="xs" color="gray.600" mt={1}>
                Un administrador de Palomma revisará el cambio.
              </Text>
            </Box>
            <Button
              size="sm"
              variant="outline"
              flexShrink={0}
              loading={cancelRequest.isPending}
              disabled={!merchant}
              onClick={() => {
                if (!merchant) return;
                cancelRequest.mutate({
                  rentalsMerchantId: merchant.id,
                  id: pending.id,
                });
              }}
            >
              Cancelar solicitud
            </Button>
          </HStack>
        </Box>
      ) : (
        <VStack gap={5} align="stretch">
          <Box>
            <Text
              fontSize="xs"
              fontWeight={500}
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="0.04em"
              mb={2.5}
            >
              Datos de la cuenta
            </Text>
            <HStack gap={3} align="flex-start" flexWrap="wrap">
              <Box flex="1.4" minWidth="220px">
                <OnboardingComboBox
                  label="Banco"
                  items={catalogs?.banks ?? []}
                  value={formData.bank}
                  onValueChange={(value) =>
                    setFormData({ ...formData, bank: value })
                  }
                  placeholder="Buscar banco..."
                />
              </Box>
              <Box flex="1" minWidth="140px">
                <OnboardingSelect
                  label="Tipo de cuenta"
                  collection={tipoCuentaCollection}
                  value={formData.accountType}
                  onValueChange={({ value }) =>
                    setFormData({ ...formData, accountType: value[0] })
                  }
                />
              </Box>
              <Box flex="1" minWidth="160px">
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  color="gray.700"
                  mb={1.5}
                >
                  Número de cuenta
                </Text>
                <Input
                  value={formData.accountNumber}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      accountNumber: e.target.value.replace(/\D/g, ""),
                    })
                  }
                  placeholder="00000000000"
                />
              </Box>
            </HStack>
          </Box>

          <Box>
            <Text
              fontSize="xs"
              fontWeight={500}
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="0.04em"
              mb={2.5}
            >
              Titular de la cuenta
            </Text>
            <HStack gap={3} align="flex-start" flexWrap="wrap">
              <Box flex="1.4" minWidth="220px">
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  color="gray.700"
                  mb={1.5}
                >
                  Nombre del titular
                </Text>
                <Input
                  value={formData.accountHolderName}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      accountHolderName: e.target.value,
                    })
                  }
                  placeholder="Nombre completo o razón social"
                />
              </Box>
              <Box flex="1" minWidth="140px">
                <OnboardingSelect
                  label="Tipo de documento"
                  collection={tipoDocumentoTitularCollection}
                  value={formData.accountHolderDocumentType}
                  onValueChange={({ value }) =>
                    setFormData({
                      ...formData,
                      accountHolderDocumentType: value[0],
                    })
                  }
                />
              </Box>
              <Box flex="1" minWidth="160px">
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  color="gray.700"
                  mb={1.5}
                >
                  Número de documento
                </Text>
                <Input
                  value={formData.accountHolderDocumentNumber}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      accountHolderDocumentNumber: e.target.value.replace(
                        /\D/g,
                        ""
                      ),
                    })
                  }
                  placeholder="1234567890"
                />
              </Box>
            </HStack>
          </Box>

          <Box>
            <Text
              fontSize="xs"
              fontWeight={500}
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="0.04em"
              mb={2.5}
            >
              Certificación bancaria
            </Text>
            <HStack gap={3} align="center">
              <Box
                role="button"
                tabIndex={0}
                flex="1"
                minWidth={0}
                textAlign="left"
                borderWidth="1px"
                borderColor={certificationFile ? "green.200" : "gray.200"}
                bg={certificationFile ? "green.50" : "white"}
                borderRadius="md"
                px={3}
                py={2}
                cursor="pointer"
                transition="background 0.12s ease, border-color 0.12s ease"
                _hover={{
                  borderColor: certificationFile ? "green.300" : "gray.300",
                  bg: certificationFile ? "green.50" : "gray.50",
                }}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  hidden
                  onChange={(e) => {
                    setCertificationFile(e.target.files?.[0] ?? null);
                    e.target.value = "";
                  }}
                />
                <HStack gap={2.5} align="center" width="100%">
                  <Box
                    width="36px"
                    height="36px"
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor={certificationFile ? "green.200" : "gray.200"}
                    bg={certificationFile ? "white" : "gray.50"}
                    flexShrink={0}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    color={certificationFile ? "green.600" : "gray.400"}
                  >
                    <FileText size={18} />
                  </Box>
                  <Box flex="1" minWidth={0}>
                    <Text fontSize="sm" fontWeight={500} truncate>
                      {certificationFile
                        ? certificationFile.name
                        : "Ningún archivo seleccionado"}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {certificationFile
                        ? `PDF · ${formatFileSize(certificationFile.size)}`
                        : "PDF de titularidad"}
                    </Text>
                  </Box>
                  <Text
                    fontSize="xs"
                    fontWeight={500}
                    color="palomma.600"
                    flexShrink={0}
                  >
                    {certificationFile ? "Cambiar" : "Subir"}
                  </Text>
                </HStack>
              </Box>
              <Button
                loading={isSubmitting}
                disabled={!isFormValid()}
                onClick={handleSubmit}
                flexShrink={0}
              >
                Proponer cambio
              </Button>
            </HStack>
              </Box>
        </VStack>
      )}
      {merchant?.gateway && pseServiceCode && (
        <HStack
          align="flex-start"
          gap={3}
          borderWidth="1px"
          borderColor="palomma.200"
          bg="palomma.50"
          borderRadius="md"
          px={4}
          py={3}
        >
          <Box color="palomma.600" flexShrink={0} mt={0.5}>
            <Info size={16} />
          </Box>
          <Box>
            <Text fontSize="sm" fontWeight={600} color="gray.700">
              Tus pagos por PSE no llegan a la cuenta bancaria registrada con
              Palomma.
            </Text>
            <Text fontSize="sm" color="gray.600" mt={1}>
              Tus pagos entran directo a tu banco por tu convenio PSE, con el
              código de servicio{" "}
              <Text as="span" fontFamily="mono" fontWeight={600}>
                {pseServiceCode}
              </Text>
              . Puedes modificar la cuenta bancaria asociada al código de
              servicio en el{" "}
              <Link
                href={ONBOARDING_LINKS.psePortal}
                target="_blank"
                rel="noreferrer"
                color="palomma.700"
                textDecoration="underline"
              >
                Portal PSE
              </Link>
              . Si necesitas ayuda, o quieres cambiar el código de servicio
              activo en Palomma,{" "}
              <Link
                href={WHATSAPP_SUPPORT_URL}
                target="_blank"
                rel="noreferrer"
                color="palomma.700"
                textDecoration="underline"
              >
                escríbenos
              </Link>
              .
            </Text>
          </Box>
        </HStack>
      )}

    </VStack>
  );
};

export default BankAccountTab;
